import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import rbacApi from '../../api/rbacApi';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Avatar,
  Divider,
  Grid,
  Paper,
  Chip,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Warning,
  Person,
  Security,
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  ArrowForward,
  ArrowBack
} from '@mui/icons-material';

interface InvitationData {
  id: string;
  email: string;
  role: string;
  organisationId: string;
  userGroupId?: string;
  projectId?: string;
  permissions: string[];
  message?: string;
  expiresAt: string;
}

const InvitationAcceptance: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [expired, setExpired] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
    email: ''
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [accepting, setAccepting] = useState(false);

  // Validate invitation token on component mount
  useEffect(() => {
    console.log('🔗 Invitation token from URL:', token);
    console.log('🌐 Current URL:', window.location.href);
    
    if (token) {
      validateInvitationToken();
    } else {
      setError('No invitation token provided.');
      setLoading(false);
    }
  }, [token]);

  const validateInvitationToken = async () => {
    try {
      setValidating(true);
      setError(null);
      
      const response = await rbacApi.validateInvitationToken(token || '');
      
      if (response && response.invitation) {
        setInvitation(response.invitation);
        setFormData(prev => ({ ...prev, email: response.invitation.email }));
        
        // Check if invitation is expired
        if (new Date(response.invitation.expiresAt) < new Date()) {
          setExpired(true);
        }
      } else {
        setError('Invalid invitation token.');
      }
    } catch (err: any) {
      console.error('Error validating invitation:', err);
      setError(err.message || 'Failed to validate invitation token.');
    } finally {
      setLoading(false);
      setValidating(false);
    }
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleAcceptInvitation = async () => {
    try {
      setAccepting(true);
      setError(null);
      
      // Validate form data
      if (!formData.firstName.trim() || !formData.lastName.trim()) {
        setError('First name and last name are required.');
        return;
      }
      
      if (formData.password.length < 8) {
        setError('Password must be at least 8 characters long.');
        return;
      }
      
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
      
      if (formData.email !== invitation?.email) {
        setError('Email address must match the invitation.');
        return;
      }

      const acceptanceData = {
        token: token || '',
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        password: formData.password,
        email: formData.email
      };

      const response = await rbacApi.acceptInvitation(acceptanceData);
      
      if (response && response.message) {
        // Success - redirect to login or dashboard
        navigate('/login', { 
          state: { 
            message: 'Invitation accepted successfully! Please log in with your new credentials.',
            email: formData.email 
          } 
        });
      }
    } catch (err: any) {
      console.error('Error accepting invitation:', err);
      setError(err.message || 'Failed to accept invitation.');
    } finally {
      setAccepting(false);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error';
      case 'manager': return 'warning';
      case 'user': return 'success';
      case 'viewer': return 'info';
      default: return 'default';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin': return <Security />;
      case 'manager': return <Person />;
      case 'user': return <Person />;
      case 'viewer': return <Person />;
      default: return <Person />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading || validating) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Validating invitation...
        </Typography>
      </Box>
    );
  }

  if (error && !invitation) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
        gap={3}
        p={3}
      >
        <Card sx={{ maxWidth: 500, width: '100%' }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Avatar sx={{ bgcolor: 'error.main' }}>
                <Error />
              </Avatar>
              <Typography variant="h5">Invalid Invitation</Typography>
            </Box>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate('/login')}
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (expired) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
        gap={3}
        p={3}
      >
        <Card sx={{ maxWidth: 500, width: '100%' }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Avatar sx={{ bgcolor: 'warning.main' }}>
                <Warning />
              </Avatar>
              <Typography variant="h5">Invitation Expired</Typography>
            </Box>
            <Alert severity="warning" sx={{ mb: 3 }}>
              This invitation has expired. Please contact your administrator for a new invitation.
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Expired on: {invitation && formatDate(invitation.expiresAt)}
            </Typography>
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate('/login')}
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      p={3}
      sx={{ backgroundColor: '#f5f5f5' }}
    >
      <Card sx={{ maxWidth: 800, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Box display="flex" alignItems="center" gap={2} mb={4}>
            <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
              <CheckCircle />
            </Avatar>
            <Box>
              <Typography variant="h4" color="text.primary">
                Accept Invitation
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Complete your account setup to join the organization
              </Typography>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Invitation Details */}
          {invitation && (
            <Paper sx={{ p: 3, mb: 4, backgroundColor: '#f8f9fa' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Invitation Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Email fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Email: {invitation.email}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Security fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Role: 
                    </Typography>
                    <Chip
                      icon={getRoleIcon(invitation.role)}
                      label={invitation.role}
                      color={getRoleColor(invitation.role) as any}
                      size="small"
                    />
                  </Box>
                </Grid>
                {invitation.permissions.length > 0 && (
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Lock fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        Permissions: {invitation.permissions.join(', ')}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {invitation.message && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      "{invitation.message}"
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          )}

          {/* Setup Steps */}
          <Stepper activeStep={activeStep} orientation="vertical">
            <Step>
              <StepLabel>Personal Information</StepLabel>
              <StepContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      disabled
                      helperText="Email address cannot be changed"
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={!formData.firstName.trim() || !formData.lastName.trim()}
                  >
                    Continue
                    <ArrowForward sx={{ ml: 1 }} />
                  </Button>
                </Box>
              </StepContent>
            </Step>
            
            <Step>
              <StepLabel>Create Password</StepLabel>
              <StepContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      InputProps={{
                        endAdornment: (
                          <Button
                            onClick={() => setShowPassword(!showPassword)}
                            sx={{ minWidth: 'auto' }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </Button>
                        ),
                      }}
                      helperText="Password must be at least 8 characters long"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Confirm Password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      required
                      InputProps={{
                        endAdornment: (
                          <Button
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            sx={{ minWidth: 'auto' }}
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </Button>
                        ),
                      }}
                      error={formData.password !== formData.confirmPassword && formData.confirmPassword !== ''}
                      helperText={
                        formData.password !== formData.confirmPassword && formData.confirmPassword !== ''
                          ? 'Passwords do not match'
                          : ''
                      }
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    sx={{ mr: 1 }}
                  >
                    <ArrowBack sx={{ mr: 1 }} />
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={
                      formData.password.length < 8 || 
                      formData.password !== formData.confirmPassword ||
                      !formData.confirmPassword
                    }
                  >
                    Continue
                    <ArrowForward sx={{ ml: 1 }} />
                  </Button>
                </Box>
              </StepContent>
            </Step>
            
            <Step>
              <StepLabel>Review & Accept</StepLabel>
              <StepContent>
                <Paper sx={{ p: 3, backgroundColor: '#f8f9fa', mb: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Review Your Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Name:</strong> {formData.firstName} {formData.lastName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Email:</strong> {formData.email}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Role:</strong> {invitation?.role}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Organization:</strong> {localStorage.getItem('org_name') || 'Your Organization'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    sx={{ mr: 1 }}
                  >
                    <ArrowBack sx={{ mr: 1 }} />
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleAcceptInvitation}
                    disabled={accepting}
                    sx={{
                      backgroundColor: '#4CAF50',
                      '&:hover': { backgroundColor: '#45a049' }
                    }}
                  >
                    {accepting ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                        Accepting...
                      </>
                    ) : (
                      'Accept Invitation'
                    )}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          </Stepper>
        </CardContent>
      </Card>
    </Box>
  );
};

export default InvitationAcceptance; 